import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, SlidersHorizontal, X, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import PropertyCard from '../components/PropertyCard';
import { getProperties, getRegions, getActiveCountries } from '../services/api';
import { useCurrency } from '../context/CurrencyContext';

export default function PropertiesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [regions, setRegions] = useState([]);
  const [allPartners, setAllPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { formatPrice } = useCurrency();

  // Filter states
  const [filters, setFilters] = useState({
    region: searchParams.get('region') || '',
    country: searchParams.get('country') || '',
    propertyType: searchParams.get('property_type') || '',
    minPrice: searchParams.get('min_price') || '',
    maxPrice: searchParams.get('max_price') || '',
    search: searchParams.get('search') || '',
    developer: searchParams.get('partner') || searchParams.get('developer') || '',
  });

  const propertyTypes = ['Villa', 'Apartment', 'Penthouse', 'Townhouse', 'Estate', 'House', 'Condo'];
  const priceRanges = [
    { label: 'Under $500K', min: 0, max: 500000 },
    { label: '$500K - $1M', min: 500000, max: 1000000 },
    { label: '$1M - $5M', min: 1000000, max: 5000000 },
    { label: '$5M - $10M', min: 5000000, max: 10000000 },
    { label: 'Over $10M', min: 10000000, max: null },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const regionsData = await getRegions();
        setRegions(regionsData.regions || []);
      } catch (error) {
        console.error('Error fetching regions:', error);
      }
    };
    fetchData();
    // Fetch partner names for filtering
    fetch(`/api/partners/list`)
      .then(r => r.json())
      .then(d => setAllPartners([...new Set((d.partners || []).map(p => p.company || p.name))].sort()))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      try {
        const data = await getProperties({
          region: filters.region,
          country: filters.country,
          minPrice: filters.minPrice,
          maxPrice: filters.maxPrice,
          propertyType: filters.propertyType,
          search: filters.search,
        });
        // Client-side partner filter
        const filtered = filters.developer ? data.filter(p => (p.partner || p.developer) === filters.developer) : data;
        setProperties(filtered);
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, [filters]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    // Update URL params
    const params = new URLSearchParams();
    if (newFilters.region) params.set('region', newFilters.region);
    if (newFilters.country) params.set('country', newFilters.country);
    setSearchParams(params);
  };

  const handlePriceRangeChange = (range) => {
    if (range === 'all') {
      setFilters({ ...filters, minPrice: '', maxPrice: '' });
    } else {
      const [min, max] = range.split('-');
      setFilters({ ...filters, minPrice: min, maxPrice: max || '' });
    }
  };

  const clearFilters = () => {
    setFilters({
      region: '', country: '', propertyType: '', minPrice: '', maxPrice: '', search: '', developer: '',
    });
    setSearchParams({});
  };

  const activeFilterCount = Object.values(filters).filter(v => v).length;

  const FilterContent = () => (
    <div className="space-y-6">
      {/* Region */}
      <div>
        <Label className="text-white mb-2 block">Region</Label>
        <Select value={filters.region} onValueChange={(v) => handleFilterChange('region', v)}>
          <SelectTrigger className="bg-[#1A1D24] border-[#2A2F3A] text-white" data-testid="filter-region">
            <SelectValue placeholder="All Regions" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">
            <SelectItem value="all" className="text-white hover:bg-[#2A2F3A]">All Regions</SelectItem>
            {regions.map((region) => (
              <SelectItem key={region.id} value={region.name} className="text-white hover:bg-[#2A2F3A]">
                {region.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Country */}
      <div>
        <Label className="text-white mb-2 block">Country</Label>
        <Select value={filters.country} onValueChange={(v) => handleFilterChange('country', v)}>
          <SelectTrigger className="bg-[#1A1D24] border-[#2A2F3A] text-white" data-testid="filter-country">
            <SelectValue placeholder="All Countries" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">
            <SelectItem value="all" className="text-white hover:bg-[#2A2F3A]">All Countries</SelectItem>
            {regions.flatMap(r => r.countries).map((country) => (
              <SelectItem key={country} value={country} className="text-white hover:bg-[#2A2F3A]">
                {country}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Property Type */}
      <div>
        <Label className="text-white mb-2 block">Property Type</Label>
        <Select value={filters.propertyType} onValueChange={(v) => handleFilterChange('propertyType', v)}>
          <SelectTrigger className="bg-[#1A1D24] border-[#2A2F3A] text-white" data-testid="filter-type">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">
            <SelectItem value="all" className="text-white hover:bg-[#2A2F3A]">All Types</SelectItem>
            {propertyTypes.map((type) => (
              <SelectItem key={type} value={type} className="text-white hover:bg-[#2A2F3A]">
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Price Range */}
      <div>
        <Label className="text-white mb-2 block">Price Range</Label>
        <Select 
          value={filters.minPrice && filters.maxPrice ? `${filters.minPrice}-${filters.maxPrice}` : filters.minPrice ? `${filters.minPrice}-` : 'all'} 
          onValueChange={handlePriceRangeChange}
        >
          <SelectTrigger className="bg-[#1A1D24] border-[#2A2F3A] text-white" data-testid="filter-price">
            <SelectValue placeholder="All Prices" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">
            <SelectItem value="all" className="text-white hover:bg-[#2A2F3A]">All Prices</SelectItem>
            {priceRanges.map((range) => (
              <SelectItem 
                key={range.label} 
                value={`${range.min}-${range.max || ''}`}
                className="text-white hover:bg-[#2A2F3A]"
              >
                {range.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Partner */}
      {allPartners.length > 0 && (
        <div>
          <Label className="text-white mb-2 block">Partner</Label>
          <Select value={filters.developer || 'all'} onValueChange={(v) => handleFilterChange('developer', v === 'all' ? '' : v)}>
            <SelectTrigger className="bg-[#1A1D24] border-[#2A2F3A] text-white" data-testid="filter-partner">
              <SelectValue placeholder="All Partners" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">
              <SelectItem value="all" className="text-white hover:bg-[#2A2F3A]">All Partners</SelectItem>
              {allPartners.map(d => (
                <SelectItem key={d} value={d} className="text-white hover:bg-[#2A2F3A]">{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {activeFilterCount > 0 && (
        <Button 
          variant="ghost" 
          onClick={clearFilters}
          className="w-full text-[#D4AF37] hover:text-[#F7E7CE] hover:bg-[#D4AF37]/10"
          data-testid="clear-filters-btn"
        >
          <X className="h-4 w-4 mr-2" />
          Clear All Filters
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0B0C10] pt-20" data-testid="properties-page">
      {/* Header */}
      <section className="py-16 border-b border-[#2A2F3A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="label-overline">Premium Collection</span>
            <h1 className="text-4xl sm:text-5xl text-white mt-4 mb-4">Investment Properties</h1>
            <p className="text-[#A1A1AA] max-w-2xl mx-auto">
              Explore our curated selection of visa-eligible properties across 8 countries. 
              Each property is carefully vetted to meet residency requirements.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters & Results */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Desktop Filters */}
            <aside className="hidden lg:block w-72 flex-shrink-0">
              <div className="sticky top-28 bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-6">
                <h3 className="text-white font-medium mb-6 flex items-center">
                  <SlidersHorizontal className="h-5 w-5 mr-2" />
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="ml-auto bg-[#D4AF37] text-[#0B0C10] text-xs px-2 py-1 rounded-full">
                      {activeFilterCount}
                    </span>
                  )}
                </h3>
                <FilterContent />
              </div>
            </aside>

            {/* Mobile Filter Button */}
            <div className="lg:hidden flex items-center justify-between mb-4">
              <p className="text-[#A1A1AA]">
                {properties.length} properties found
              </p>
              <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
                <SheetTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="border-[#2A2F3A] text-white"
                    data-testid="mobile-filter-btn"
                  >
                    <SlidersHorizontal className="h-4 w-4 mr-2" />
                    Filters
                    {activeFilterCount > 0 && (
                      <span className="ml-2 bg-[#D4AF37] text-[#0B0C10] text-xs px-2 py-0.5 rounded-full">
                        {activeFilterCount}
                      </span>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent className="bg-[#0B0C10] border-[#2A2F3A]">
                  <SheetHeader>
                    <SheetTitle className="text-white">Filters</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6">
                    <FilterContent />
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            {/* Results */}
            <div className="flex-1">
              <div className="hidden lg:flex items-center justify-between mb-8">
                <p className="text-[#A1A1AA]">
                  {properties.length} properties found
                </p>
              </div>

              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="bg-[#1A1D24] rounded-xl h-96 animate-pulse" />
                  ))}
                </div>
              ) : properties.length === 0 ? (
                <div className="text-center py-16">
                  <MapPin className="h-12 w-12 text-[#A1A1AA] mx-auto mb-4" />
                  <h3 className="text-xl text-white mb-2">No properties found</h3>
                  <p className="text-[#A1A1AA] mb-6">
                    Try adjusting your filters to see more results
                  </p>
                  <Button onClick={clearFilters} className="btn-luxury">
                    Clear Filters
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {properties.map((property, index) => (
                    <motion.div
                      key={property.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <PropertyCard property={property} />
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
